import { useTranslation } from 'react-i18next';

import { ImagePlaceholder } from '@/components/ImagePlaceholder';

/**
 * DealMap — Spec V1.1 §M4 L624.
 *
 * Renders a Google Maps Embed iframe centred on the deal's coordinates when
 * both `latitude` and `longitude` are present and the build-time API key
 * `VITE_GOOGLE_MAPS_EMBED_KEY` is set. Falls back to a gracious
 * `<ImagePlaceholder />` + hint pair otherwise, so the page layout never
 * collapses on missing coords / missing key (UB §3.2 paragraph on empty
 * sub-blocks).
 *
 * Why iframe (and not a JS SDK / Leaflet / Mapbox): CDC §7.4 L315 caps the
 * scope at Embed API ; the public catalogue is excluded (Spec V1.1 §2.2
 * L122). No JS bundle impact, no API call billing (Embed is unlimited
 * free tier). See plan `contexte-sur-la-robust-wombat.md` for the
 * deliberation that ruled out alternative providers in V1.
 *
 * Why the API key is read inside the component (not at module top-level):
 * Vitest stubs `import.meta.env.VITE_*` per test via `vi.stubEnv`, but a
 * top-level capture freezes the value at module-load time so the stub has
 * no effect. Reading the env on every render is an O(1) object lookup —
 * the cost is invisible next to the iframe paint, and the testability win
 * is real (see DealMap.test.tsx).
 */
export type DealMapProps = {
  latitude: number | undefined;
  longitude: number | undefined;
  /** Building / deal title — surfaced in the iframe `title` for a11y. */
  title: string;
};

export function DealMap({ latitude, longitude, title }: DealMapProps) {
  const { t } = useTranslation();
  const googleMapsKey = import.meta.env.VITE_GOOGLE_MAPS_EMBED_KEY as string | undefined;
  const hasCoords = typeof latitude === 'number' && typeof longitude === 'number';

  if (!hasCoords || !googleMapsKey) {
    return (
      <>
        <div className="deal-location__map">
          <ImagePlaceholder label={t('deal.map.unavailable')} />
        </div>
        <div className="deal-location__meta">{t('deal.map.unavailableHint')}</div>
      </>
    );
  }

  const src = `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(
    googleMapsKey,
  )}&q=${latitude},${longitude}&zoom=15`;

  return (
    <div className="deal-location__map">
      <iframe
        title={t('deal.location.title') + ' — ' + title}
        src={src}
        width="100%"
        height="100%"
        style={{ border: 0, display: 'block' }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>
  );
}
